import { BrandPaletteEntry, BrandTheme, DashboardPage, DashboardLayout, DashboardCell } from '../types/gotypes'
import { buildColorMap } from '../components/platinum-graphs/generators/helpers'

/**
 * Brand theme resolution (#1182/#1193).
 *
 * A page (and, per #1193, a chart) stores a BrandTheme id in
 * `brand_palette_key` — a LIVE reference, never a copy of the colours, so
 * editing a theme recolours everything that references it. Resolution order:
 *
 *   chart theme → page theme → job palette → DEFAULT_COLORS
 *
 * A theme decides the colour of every brand it names; the layer below supplies
 * the brands it does not name — hence the merges here are spreads, not
 * replacements. A referenced theme that cannot be found (deleted, or not yet
 * loaded) resolves to nothing and the layer below shows through.
 */

export type ThemePaletteLookup = (id: string) => Record<string, BrandPaletteEntry> | undefined

/** Lookup over a loaded theme list (authenticated surfaces — useBrandThemes). */
export function themeLookupFromList(themes: BrandTheme[]): ThemePaletteLookup {
  return (id) => themes.find((t) => t.id === id)?.palette
}

/**
 * Lookup over the server-resolved map (`Dashboard.resolved_themes`) — the only
 * source a shared, logged-out viewer has.
 */
export function themeLookupFromResolved(
  resolved?: Record<string, Record<string, BrandPaletteEntry>>
): ThemePaletteLookup {
  return (id) => resolved?.[id]
}

/**
 * The effective palette for a page: the page's theme merged over the base
 * (job) palette. Returns undefined when neither layer has anything to say.
 */
export function resolvePagePalette(
  page: Pick<DashboardPage, 'brand_palette_key'> | undefined,
  lookupTheme: ThemePaletteLookup,
  basePalette?: Record<string, BrandPaletteEntry>
): Record<string, BrandPaletteEntry> | undefined {
  const themePalette = page?.brand_palette_key ? lookupTheme(page.brand_palette_key) : undefined
  if (themePalette && basePalette) return { ...basePalette, ...themePalette }
  return themePalette || basePalette
}

/** `resolvePagePalette` flattened into the colour map the chart generators take. */
export function resolvePageColorMap(
  page: Pick<DashboardPage, 'brand_palette_key'> | undefined,
  lookupTheme: ThemePaletteLookup,
  basePalette?: Record<string, BrandPaletteEntry>
): Record<string, string> | undefined {
  return buildColorMap(resolvePagePalette(page, lookupTheme, basePalette))
}

/**
 * Every theme flattened to the colour-map shape, keyed by theme id — the form
 * a chart widget's renderer needs to overlay its own theme (#1193) on top of
 * the page-level map it is handed.
 */
export function themeColorMapsFromList(themes: BrandTheme[]): Record<string, Record<string, string>> {
  const maps: Record<string, Record<string, string>> = {}
  for (const theme of themes) {
    const map = buildColorMap(theme.palette)
    if (map) maps[theme.id] = map
  }
  return maps
}

/** Same, from the server-resolved `Dashboard.resolved_themes` map. */
export function themeColorMapsFromResolved(
  resolved?: Record<string, Record<string, BrandPaletteEntry>>
): Record<string, Record<string, string>> {
  const maps: Record<string, Record<string, string>> = {}
  for (const [id, palette] of Object.entries(resolved || {})) {
    const map = buildColorMap(palette)
    if (map) maps[id] = map
  }
  return maps
}

/**
 * "Reset all charts on page" (#1175, Matt 1 Sep): strips the chart-level
 * theme reference (`table_graph_data.brand_palette_key`) from every cell in a
 * layout — nested column layouts included — so each chart follows the page
 * theme again. Pure: returns a new layout and the number of charts reset;
 * returns the SAME layout object when nothing changed, so callers can skip a
 * no-op save.
 */
export function resetChartThemesInLayout(layout: DashboardLayout | undefined): { layout: DashboardLayout | undefined; count: number } {
  if (!layout?.rows) return { layout, count: 0 }
  let count = 0
  const mapCell = (cell: DashboardCell): DashboardCell => {
    let next = cell
    if (cell.table_graph_data?.brand_palette_key) {
      count += 1
      const { brand_palette_key: _dropped, ...rest } = cell.table_graph_data
      next = { ...next, table_graph_data: rest }
    }
    if (cell.layout_data?.rows) {
      const inner = resetChartThemesInLayout(cell.layout_data)
      if (inner.count > 0) {
        count += inner.count
        next = { ...next, layout_data: inner.layout }
      }
    }
    return next
  }
  const rows = layout.rows.map((row) => {
    const cells = (row.cells || []).map(mapCell)
    return cells.some((c, i) => c !== row.cells[i]) ? { ...row, cells } : row
  })
  if (count === 0) return { layout, count: 0 }
  return { layout: { ...layout, rows }, count }
}

/**
 * Suggested name for a duplicated theme (#1264): `<source> copy`, then
 * `<source> copy 2`, `copy 3`… — the first that no existing theme already
 * holds. Compared case-insensitively and whitespace-trimmed, because that is
 * how both the server's by-name upsert (#973) and the DB's unique index
 * (migration 000115) compare, and a collision would either silently overwrite
 * the source or fail hard. Duplicating a copy strips its own `copy N` suffix
 * first so names do not stack into "copy copy".
 */
export function uniqueCopyName(sourceName: string, existingNames: string[]): string {
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()))
  const base = sourceName.trim().replace(/\s+copy(?:\s+\d+)?$/i, '')
  const candidate = (n: number) => (n === 1 ? `${base} copy` : `${base} copy ${n}`)
  let n = 1
  while (taken.has(candidate(n).toLowerCase())) n++
  return candidate(n)
}
